import { Check } from 'lucide-react'
import { cn } from '../../../utils/cn'

interface AssessmentSection {
  id: string
  label: string
  isCompleted?: boolean
}

interface AssessmentSidebarProps {
  sections: AssessmentSection[]
  activeSection: string
  sectionsCompleted: number
  totalSections: number
  progressPercentage: number
  onSectionClick?: (sectionId: string) => void
  className?: string
}

export function AssessmentSidebar({
  sections,
  activeSection,
  sectionsCompleted,
  totalSections,
  progressPercentage,
  onSectionClick,
  className,
}: AssessmentSidebarProps) {
  // Ensure progressPercentage is a valid number
  const progressPct = typeof progressPercentage === 'number' && !isNaN(progressPercentage)
    ? progressPercentage
    : Number(progressPercentage) || 0

  return (
    <aside
      className={cn(
        'flex h-full w-full lg:w-[242px] flex-col',
        className
      )}
    >
      {/* Sections List */}
      <div className='flex-1 bg-[#F5F6F8] rounded-[19px] px-4 py-7'>
        <nav className='space-y-6'>
          {sections.map(section => {
            const isActive = activeSection === section.id
            const isCompleted = section.isCompleted

            return (
              <button
                key={section.id}
                onClick={() => onSectionClick?.(section.id)}
                className='relative flex w-full items-center justify-between text-left'
              >
                {/* Active indicator - orange bar on left */}
                {isActive && (
                  <div className='absolute -left-4 top-1/2 h-[31px] w-[3px] -translate-y-1/2 bg-[#FF6600]' />
                )}

                <span
                  className={cn(
                    'text-[14px] font-bold leading-[21px] text-[#151515]'
                  )}
                >
                  {section.label}
                </span>

                {/* Status indicator - radio button style */}
                {isCompleted ? (
                  <div className='flex h-[24px] w-[24px] items-center justify-center rounded-full bg-[#0ECF5B]'>
                    <Check className='h-3 w-3 text-white' strokeWidth={3} />
                  </div>
                ) : (
                  <div className='h-[24px] w-[24px] rounded-full border-2 border-[#4e4d4d]' />
                )}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Progress Footer */}
      <div className='bg-white rounded-[8px] px-4 py-2 mt-4'>
        <div className='mb-3 rounded-full border border-[#434343] py-1 text-center text-[12px] font-normal text-[#434343]'>
          {sectionsCompleted}/{totalSections} sections completed
        </div>
        <div className='flex items-center justify-between'>
          <span className='text-[14px] font-normal text-[#434343]'>Progress Status</span>
          <div className='relative w-[32px] h-[32px]'>
            {/* Background circle */}
            <svg className='w-full h-full -rotate-90' viewBox='0 0 32 32'>
              <circle
                cx='16'
                cy='16'
                r='14'
                fill='none'
                stroke='#E0E0E0'
                strokeWidth='3'
              />
              <circle
                cx='16'
                cy='16'
                r='14'
                fill='none'
                stroke='#FF6600'
                strokeWidth='3'
                strokeDasharray={`${(progressPct / 100) * 88} 88`}
                strokeLinecap='round'
              />
            </svg>
            {/* Percentage text */}
            <span className='absolute inset-0 flex items-center justify-center text-[10px] font-bold text-[#151515]'>
              {progressPct.toFixed(0)}%
            </span>
          </div>
        </div>
      </div>
    </aside>
  )
}
